import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { object, string } from "valibot";
import { dataName, label, rememberInput, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { DECORATIVE_IMAGE_ALT, TEXT_CONDITION_KEYS } from "../../constants";
import { defaultStringFilter } from "../../pages/customer/form/filter";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { PreviewContentLabel } from "../FieldPreviewComponents";
import { LocalImage } from "../../components/image/Image";
import { keyFromSelector } from "i18next";
import fieldMapPreview from "../../assets/editor/field-map-preview.webp";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  required: required(),
  remember_input: rememberInput(),
});
const ValueSchema = string();

export type CurrentLocationFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const CurrentLocationField: Field<CurrentLocationFieldProperties, DefaultDtoType, ValueType> = {
  type: "currentLocation",
  category: "location",
  widget: "com.dirkjanhoek:currentlocation:4",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    required: false,
    remember_input: false,
  }),
  ruleBehaviour: {
    conditions: { keys: TEXT_CONDITION_KEYS, variant: "text" },
    actions: { variant: "text" },
  },
  filter: {
    type: "string",
    definition: defaultStringFilter,
  },
  stringValue: (value) => value,
  renderValue: (value) => value,
  icon: "gpsLocation",
  titleKey: keyFromSelector(($) => $.CURRENT_LOCATION_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.CURRENT_LOCATION_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <LocalImage size="lg" cover src={fieldMapPreview} alt={DECORATIVE_IMAGE_ALT} />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(CurrentLocationField);
